import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Switch,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Basemap } from "components/basemap/basemap";
import { Binary, BarChart3 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MapLayerMouseEvent, MapRef } from "react-map-gl";
import { Link as RouterLink } from "react-router-dom";
import { ChartsPanel } from "./ChartsPanel";
import { DATA_LAYERS, FEATURE_INSPECTOR_CONFIG, LAYER_CONFIG } from "./configs";
import { calculateEmissions, EMISSIONS_FACTORS } from "./emissions-utils";
import {
  createInitialControlState,
  createVectorTileSource,
  DEMO_COORDINATES,
  getLayerBase,
  getLayerHeight,
} from "./map-utils";
import {
  FeaturePropertyValue,
  HighlightedFeature,
  LayerControlState,
  OrderedTable,
  SelectedFeature,
  TableConfig,
} from "./types";

const getFeatureInspectorConfig = (layerName: string) => {
  // Map layer IDs to config keys
  const layerConfigMap: Record<string, string> = {
    "generators-layer": "generators",
  };

  const configKey = layerConfigMap[layerName] || layerName || "general";
  return (
    FEATURE_INSPECTOR_CONFIG[configKey] || FEATURE_INSPECTOR_CONFIG.general
  );
};

const groupPropertiesByTable = (
  properties: Record<string, FeaturePropertyValue>,
  layerName?: string
): Record<string, Record<string, FeaturePropertyValue>> => {
  const grouped: Record<string, Record<string, FeaturePropertyValue>> = {};

  // Special handling for generators
  if (layerName === "generators-layer") {
    grouped["generator"] = {};
    grouped["emissions"] = {};
    grouped["statistics"] = {};

    // Generator info fields
    const generatorFields = ["duid", "facility_name", "facility_code", "network", "region", "fueltech", "capacity", "status", "current_output", "current_timestamp"];
    // Statistics fields
    const statsFields = ["timeseries_count", "avg_output_mw", "max_output_mw", "min_output_mw", "first_timestamp", "last_timestamp"];
    // Emissions fields
    const emissionsFields = ["emissions_factor", "current_emissions_rate", "estimated_hourly_emissions"];

    Object.entries(properties).forEach(([key, value]) => {
      if (generatorFields.includes(key)) {
        grouped["generator"][key] = value;
      } else if (statsFields.includes(key)) {
        grouped["statistics"][key] = value;
      } else if (emissionsFields.includes(key)) {
        grouped["emissions"][key] = value;
      }
    });

    return grouped;
  }

  // Default handling for other layers
  const defaultTableName = layerName || "general";

  Object.entries(properties).forEach(([key, value]) => {
    if (key.includes("___")) {
      const [tableName, fieldName] = key.split("___");
      if (!grouped[tableName]) {
        grouped[tableName] = {};
      }
      grouped[tableName][fieldName] = value;
    } else {
      if (!grouped[defaultTableName]) {
        grouped[defaultTableName] = {};
      }
      grouped[defaultTableName][key] = value;
    }
  });

  return grouped;
};

const getOrderedTables = (
  groupedProperties: Record<string, Record<string, FeaturePropertyValue>>,
  layerName?: string
): OrderedTable[] => {
  const config = layerName
    ? getFeatureInspectorConfig(layerName)
    : FEATURE_INSPECTOR_CONFIG.general;

  if (!config) {
    return [];
  }

  const tables = config.tables || [];
  const excludedTables = config.excludedTables || [];

  const filteredProperties = Object.fromEntries(
    Object.entries(groupedProperties).filter(
      ([tableName]) => !excludedTables.includes(tableName)
    )
  );

  const configMap = new Map(
    tables.map((table: TableConfig) => [table.key, table])
  );

  const orderedTables: OrderedTable[] = [];

  for (const tableConfig of tables) {
    if (filteredProperties[tableConfig.key]) {
      if ("attributes" in tableConfig && tableConfig.attributes) {
        const orderedFields: Record<string, FeaturePropertyValue> = {};
        const availableFields = filteredProperties[tableConfig.key];

        for (const attrName of tableConfig.attributes) {
          if (availableFields.hasOwnProperty(attrName)) {
            orderedFields[attrName] = availableFields[attrName];
          }
        }

        orderedTables.push({
          key: tableConfig.key,
          displayName: tableConfig.displayName,
          fields: orderedFields,
          hasAttributes: true,
        });
      } else {
        orderedTables.push({
          key: tableConfig.key,
          displayName: tableConfig.displayName,
          fields: { ERROR: "MISSING ATTRIBUTES DEFINITION!" },
          hasAttributes: false,
        });
      }
    }
  }

  for (const [tableName, tableFields] of Object.entries(filteredProperties)) {
    if (!configMap.has(tableName)) {
      orderedTables.push({
        key: tableName,
        displayName: tableName.replace(/_/g, " "),
        fields: tableFields,
        hasAttributes: false,
      });
    }
  }

  return orderedTables;
};

// Get all unique timestamps from the data
const getAllTimestamps = (geojsonData: any): string[] => {
  const timestampSet = new Set<string>();

  geojsonData.features.forEach((feature: any) => {
    feature.properties.timeseries.forEach((ts: any) => {
      timestampSet.add(ts.timestamp);
    });
  });

  return Array.from(timestampSet).sort();
};

// Process GeoJSON to get values for a specific timestamp
const processDispatchData = (geojsonData: any, timestamp?: string) => {
  const features = geojsonData.features.map((feature: any) => {
    const timeseries = feature.properties.timeseries;

    // Find data for specific timestamp or use most recent
    let dataPoint;
    if (timestamp) {
      dataPoint = timeseries.find((ts: any) => ts.timestamp === timestamp);
    }
    if (!dataPoint) {
      dataPoint = timeseries[timeseries.length - 1];
    }

    return {
      type: "Feature",
      geometry: feature.geometry,
      properties: {
        ...feature.properties,
        current_output: dataPoint.value,
        current_timestamp: dataPoint.timestamp,
      },
    };
  });

  return {
    type: "FeatureCollection",
    features,
  };
};

// Fuel tech color mapping
const FUEL_TECH_COLORS: Record<string, string> = {
  solar_utility: "#FFD700",
  wind: "#4CAF50",
  battery_charging: "#2196F3",
  battery_discharging: "#FF6B6B",
  coal_black: "#424242",
  coal_brown: "#8D6E63",
  gas_ccgt: "#FF9800",
  gas_ocgt: "#FFA726",
  gas_recip: "#FFB74D",
  gas_steam: "#FFCC80",
  hydro: "#00BCD4",
  pumps: "#0097A7",
  distillate: "#9C27B0",
  bioenergy_biomass: "#8BC34A",
};

export const ElectricityMarket = () => {
  const mapRef = useRef<MapRef>(null);
  const theme = useTheme();

  const [coordinates] = useState(DEMO_COORDINATES);
  const [dynamicZoom, setDynamicZoom] = useState(4);
  const [layersEnabled, setLayersEnabled] = useState<Record<string, boolean>>(
    DATA_LAYERS.reduce(
      (acc, layer) => ({ ...acc, [layer.name]: layer.enabled }),
      {}
    )
  );
  const [layerControlState, setLayerControlState] = useState<LayerControlState>(
    createInitialControlState()
  );
  const [selectedFeature, setSelectedFeature] =
    useState<SelectedFeature | null>(null);
  const [highlightedFeature, setHighlightedFeature] =
    useState<HighlightedFeature | null>(null);
  const [rawData, setRawData] = useState<any>(null);
  const [processedData, setProcessedData] = useState<any>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [timestamps, setTimestamps] = useState<string[]>([]);
  const [currentTimestampIndex, setCurrentTimestampIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sliderMarks, setSliderMarks] = useState<Array<{ value: number; label: string | JSX.Element }>>([]);
  const [chartsPanelOpen, setChartsPanelOpen] = useState(true);
  const [precomputedChartData, setPrecomputedChartData] = useState<any>(null);

  // Load GeoJSON data and precomputed chart data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [dataResponse, precomputedResponse] = await Promise.all([
          fetch("/demos/geojson/dispatch_scada.geojson"),
          fetch("/demos/geojson/dispatch_scada_precomputed.json"),
        ]);

        const data = await dataResponse.json();
        const precomputed = await precomputedResponse.json();
        const allTimestamps = getAllTimestamps(data);

        // Create marks for the slider - every 6 hours with separate day labels
        const marks: Array<{ value: number; label: string | JSX.Element }> = [];
        const seenDays = new Map<string, number>(); // Map day to first index
        const seenSixHours = new Set<string>();

        allTimestamps.forEach((timestamp, index) => {
          // Extract date and time from timestamp (format: "2025/09/30 12:25:00")
          const [datePart, timePart] = timestamp.split(" ");
          const hour = parseInt(timePart.split(":")[0]);
          const dayKey = datePart;

          // Track first occurrence of each day
          if (!seenDays.has(dayKey)) {
            seenDays.set(dayKey, index);
          }

          // Add mark every 6 hours (00:00, 06:00, 12:00, 18:00)
          const sixHourKey = `${dayKey}_${hour}`;
          if (hour % 6 === 0 && !seenSixHours.has(sixHourKey)) {
            seenSixHours.add(sixHourKey);
            const formattedHour = `${hour.toString().padStart(2, '0')}:00`;

            // Check if this is the first timestamp of a new day
            const isNewDay = seenDays.get(dayKey) === index;

            marks.push({
              value: index,
              label: isNewDay
                ? `${formattedHour}\n${datePart.replace(/\//g, "-")}`
                : formattedHour,
            });
          }
        });

        setRawData(data);
        setPrecomputedChartData(precomputed);
        setTimestamps(allTimestamps);
        setSliderMarks(marks);
        setCurrentTimestampIndex(allTimestamps.length - 1); // Start at most recent
        setProcessedData(processDispatchData(data, allTimestamps[allTimestamps.length - 1]));
        setIsDataLoaded(true);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    loadData();
  }, []);

  // Update data when timestamp changes
  useEffect(() => {
    if (rawData && timestamps.length > 0) {
      const newData = processDispatchData(rawData, timestamps[currentTimestampIndex]);
      setProcessedData(newData);

      // Update the map source if it exists
      const map = mapRef.current?.getMap();
      if (map && map.getSource("dispatch-scada-source")) {
        (map.getSource("dispatch-scada-source") as any).setData(newData);
      }
    }
  }, [currentTimestampIndex, rawData, timestamps]);

  // Animation playback
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentTimestampIndex((prev) => {
        if (prev >= timestamps.length - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 500); // Update every 500ms

    return () => clearInterval(interval);
  }, [isPlaying, timestamps.length]);

  const onClick = async (event: MapLayerMouseEvent) => {
    const features = event.features;
    if (features && features.length > 0) {
      const feature = features[0];
      const layerId = feature.layer?.id || "Unknown";
      const layerName = layerId.replace("-layer", "");

      // Reset all layers to their default colors before highlighting new feature
      const map = mapRef.current?.getMap();
      if (map) {
        DATA_LAYERS.forEach((layer) => {
          const layerId = `${layer.name}-layer`;
          if (map.getLayer(layerId)) {
            if (layer.type === "circle") {
              map.setPaintProperty(layerId, "circle-color", layer.color);
            } else if (layer.type === "line") {
              map.setPaintProperty(layerId, "line-color", layer.color);
            } else if (layer.type === "fill") {
              map.setPaintProperty(layerId, "fill-color", layer.color);
            } else if (layer.type === "extrude") {
              map.setPaintProperty(
                layerId,
                "fill-extrusion-color",
                layer.color
              );
            }
          }
        });
      }

      if (feature.layer?.id) {
        const featureIdentifier =
          feature.id ||
          feature.properties?.duid ||
          (feature.geometry?.type === "Point"
            ? `${feature.geometry.coordinates[0]}_${feature.geometry.coordinates[1]}`
            : JSON.stringify(feature.properties).slice(0, 50));

        setHighlightedFeature({
          layerId: feature.layer.id,
          sourceId: layerId === "generators-layer" ? "dispatch-scada-source" : `${layerName}-source`,
          sourceLayer: feature.sourceLayer || "",
          featureId: featureIdentifier,
        });
      } else {
        setHighlightedFeature(null);
      }

      // Add emissions calculations to properties if it's a generator
      let enrichedProperties = feature.properties;
      if (layerId === "generators-layer" && feature.properties) {
        const fueltech = feature.properties.fueltech as string;
        const currentOutput = feature.properties.current_output as number;
        const emissionsFactor = EMISSIONS_FACTORS[fueltech] || 0;
        const currentEmissionsRate = calculateEmissions(fueltech, currentOutput, 1 / 12); // 5-minute rate
        const estimatedHourlyEmissions = calculateEmissions(fueltech, currentOutput, 1);

        enrichedProperties = {
          ...feature.properties,
          emissions_factor: `${emissionsFactor} kg CO₂e/MWh`,
          current_emissions_rate: `${currentEmissionsRate.toFixed(3)} t CO₂e/5min`,
          estimated_hourly_emissions: `${estimatedHourlyEmissions.toFixed(2)} t CO₂e/hr`,
        };
      }

      setSelectedFeature({
        layer: layerId,
        properties: enrichedProperties,
        geometry: feature.geometry,
      });
    } else {
      setSelectedFeature(null);
      setHighlightedFeature(null);
    }
  };

  const handleZoom = (zoom: number): void => {
    setDynamicZoom(zoom);
    mapRef.current?.flyTo({ zoom: zoom, duration: 500 });
  };

  const resetCompass = () => {
    if (!mapRef.current) return;
    mapRef.current.easeTo({
      bearing: 0,
      pitch: 0,
    });
  };

  const handleGeneratorClick = (duid: string) => {
    // Find the generator in the processed data
    const generator = processedData?.features.find(
      (f: any) => f.properties.duid === duid
    );

    if (generator && mapRef.current) {
      const coords = generator.geometry.coordinates;
      mapRef.current.flyTo({
        center: [coords[0], coords[1]],
        zoom: 10,
        duration: 1000,
      });

      // Select the feature
      setSelectedFeature({
        layer: "generators-layer",
        properties: generator.properties,
        geometry: generator.geometry,
      });
    }
  };

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();

    const addDataLayers = () => {
      try {
        // Add dispatch SCADA data source
        if (!map.getSource("dispatch-scada-source") && isDataLoaded && processedData) {
          map.addSource("dispatch-scada-source", {
            type: "geojson",
            data: processedData as any,
          });

          // Add circle layer for generators (radius in pixels, scaled by output)
          map.addLayer({
            id: "generators-layer",
            type: "circle",
            source: "dispatch-scada-source",
            paint: {
              "circle-radius": [
                "interpolate",
                ["linear"],
                ["zoom"],
                4, ["*", ["get", "current_output"], 0.02], // Zoomed out: smaller multiplier
                8, ["*", ["get", "current_output"], 0.05],
                12, ["*", ["get", "current_output"], 0.1],
                16, ["*", ["get", "current_output"], 0.2], // Zoomed in: larger multiplier
              ],
              "circle-color": [
                "match",
                ["get", "fueltech"],
                "solar_utility", FUEL_TECH_COLORS.solar_utility,
                "wind", FUEL_TECH_COLORS.wind,
                "battery_charging", FUEL_TECH_COLORS.battery_charging,
                "battery_discharging", FUEL_TECH_COLORS.battery_discharging,
                "coal_black", FUEL_TECH_COLORS.coal_black,
                "coal_brown", FUEL_TECH_COLORS.coal_brown,
                "gas_ccgt", FUEL_TECH_COLORS.gas_ccgt,
                "gas_ocgt", FUEL_TECH_COLORS.gas_ocgt,
                "gas_recip", FUEL_TECH_COLORS.gas_recip,
                "gas_steam", FUEL_TECH_COLORS.gas_steam,
                "hydro", FUEL_TECH_COLORS.hydro,
                "pumps", FUEL_TECH_COLORS.pumps,
                "distillate", FUEL_TECH_COLORS.distillate,
                "bioenergy_biomass", FUEL_TECH_COLORS.bioenergy_biomass,
                "#999999", // default color
              ],
              "circle-opacity": 0.8,
              "circle-stroke-width": 1,
              "circle-stroke-color": "#ffffff",
            },
          });

          // Add labels for generators (visible at zoom level 7+)
          map.addLayer({
            id: "generators-labels",
            type: "symbol",
            source: "dispatch-scada-source",
            layout: {
              "text-field": [
                "concat",
                ["get", "facility_name"],
                "\n",
                ["to-string", ["get", "current_output"]],
                " MW"
              ],
              "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
              "text-size": 11,
              "text-offset": [0, 0.5],
              "text-anchor": "top",
              "text-allow-overlap": false,
              "text-ignore-placement": false,
            },
            paint: {
              "text-color": theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
              "text-halo-color": theme.palette.mode === "dark" ? "#000000" : "#FFFFFF",
              "text-halo-width": 2,
              "text-halo-blur": 1,
            },
            minzoom: 7,
          });
        }

        const vectorLayers = DATA_LAYERS.filter(
          (layer) => layer.type !== "raster"
        );

        for (const layer of vectorLayers) {
          map.addSource(`${layer.name}-source`, createVectorTileSource(layer));

          if (layer.type === "circle") {
            map.addLayer({
              id: `${layer.name}-layer`,
              type: "circle",
              source: `${layer.name}-source`,
              "source-layer": layer.sourceLayer,
              paint: {
                "circle-radius": 4,
                "circle-color": layer.color,
                "circle-opacity": 1,
                "circle-stroke-width": 0,
                "circle-stroke-color": "#ffffff",
              },
              layout: {
                visibility: layer.enabled ? "visible" : "none",
              },
            });
          } else if (layer.type === "line") {
            map.addLayer({
              id: `${layer.name}-layer`,
              type: "line",
              source: `${layer.name}-source`,
              "source-layer": layer.sourceLayer,
              paint: {
                "line-color": layer.color,
                "line-width": 2,
                "line-opacity": 1,
              },
              layout: {
                visibility: layer.enabled ? "visible" : "none",
              },
            });
          } else if (layer.type === "fill") {
            map.addLayer({
              id: `${layer.name}-layer`,
              type: "fill",
              source: `${layer.name}-source`,
              "source-layer": layer.sourceLayer,
              paint: {
                "fill-color": layer.color,
                "fill-opacity": 0.6,
                "fill-outline-color": layer.color,
              },
              layout: {
                visibility: layer.enabled ? "visible" : "none",
              },
            });
          } else if (layer.type === "extrude") {
            map.addLayer({
              id: `${layer.name}-layer`,
              type: "fill-extrusion",
              source: `${layer.name}-source`,
              "source-layer": layer.sourceLayer,
              paint: {
                "fill-extrusion-color": layer.color,
                "fill-extrusion-height": getLayerHeight(layer) as any,
                "fill-extrusion-base": getLayerBase(layer) as any,
                "fill-extrusion-opacity": 1,
              },
              layout: {
                visibility: layer.enabled ? "visible" : "none",
              },
            });
          }
        }
      } catch (error) {
        console.error("Error adding data layers:", error);
      }
    };

    const handleMapReady = () => {
      if (map.isStyleLoaded()) {
        addDataLayers();
      }
    };

    if (map.loaded() && map.isStyleLoaded()) {
      handleMapReady();
    } else {
      map.once("load", handleMapReady);
      map.once("styledata", handleMapReady);
    }

    return () => {
      map.off("load", handleMapReady);
      map.off("styledata", handleMapReady);
    };
  }, [mapRef.current, isDataLoaded, processedData]);

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();

    Object.entries(layersEnabled).forEach(([layerName, enabled]) => {
      const layerId = `${layerName}-layer`;
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(
          layerId,
          "visibility",
          enabled ? "visible" : "none"
        );
      }
    });
  }, [layersEnabled]);

  useEffect(() => {
    if (!mapRef.current || !highlightedFeature || !selectedFeature) return;

    const map = mapRef.current.getMap();
    const layer = DATA_LAYERS.find(
      (l) => `${l.name}-layer` === highlightedFeature.layerId
    );

    if (layer) {
      const highlightColor = "#FFA500";

      if (layer.type === "circle") {
        map.setPaintProperty(
          highlightedFeature.layerId,
          "circle-color",
          highlightColor
        );
      } else if (layer.type === "line") {
        map.setPaintProperty(
          highlightedFeature.layerId,
          "line-color",
          highlightColor
        );
      } else if (layer.type === "fill") {
        map.setPaintProperty(
          highlightedFeature.layerId,
          "fill-color",
          highlightColor
        );
      } else if (layer.type === "extrude") {
        map.setPaintProperty(
          highlightedFeature.layerId,
          "fill-extrusion-color",
          highlightColor
        );
      }
    }
  }, [highlightedFeature, selectedFeature]);

  const toggleLayer = (layerName: string) => {
    setLayersEnabled((prev) => ({
      ...prev,
      [layerName]: !prev[layerName],
    }));
  };

  const handleLayerControlChange = (
    controlId: string,
    value: boolean | string
  ) => {
    setLayerControlState((prev) => ({
      ...prev,
      [controlId]: value,
    }));

    const config = LAYER_CONFIG.find((c) => c.id === controlId);
    if (config?.mapLayers && typeof value === "boolean") {
      config.mapLayers.forEach((layerName) => {
        setLayersEnabled((prev) => ({
          ...prev,
          [layerName]: value,
        }));
      });
    }
  };

  const handleRadioChange = (controlId: string, value: string) => {
    setLayerControlState((prev) => ({
      ...prev,
      [`${controlId}-radio`]: value,
    }));
  };

  const handleButtonClick = (controlId: string) => {
    // Handle button clicks here
    console.log("Button clicked:", controlId);
  };

  return (
    <>
      <Basemap
        // @ts-ignore
        mapRef={mapRef}
        attributionControl={true}
        latitude={coordinates.latitude}
        longitude={coordinates.longitude}
        zoom={dynamicZoom}
        pitch={0}
        onZoom={(zoom) => setDynamicZoom(zoom)}
        onClick={onClick}
        interactiveLayerIds={["generators-layer", ...DATA_LAYERS.map((layer) => `${layer.name}-layer`)]}
        minZoom={4}
        style={{
          width: "100%",
          height: "100vh",
          position: "absolute",
          left: 0,
          top: 0,
          backgroundColor:
            theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
        }}
        // mapStyle="BLANK"
      >
        {/* Charts Panel Toggle Button */}
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 1001,
          }}
        >
          <Button
            variant="contained"
            onClick={() => setChartsPanelOpen(!chartsPanelOpen)}
            startIcon={<BarChart3 size={20} />}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(33, 33, 33, 0.95)"
                  : "rgba(255, 255, 255, 0.95)",
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(44, 44, 44, 0.95)"
                    : "rgba(245, 245, 245, 0.95)",
              },
            }}
          >
            {chartsPanelOpen ? "Hide Charts" : "Show Charts"}
          </Button>
        </Box>

        {/* Charts Panel */}
        <ChartsPanel
          open={chartsPanelOpen}
          onClose={() => setChartsPanelOpen(false)}
          precomputedData={precomputedChartData}
          currentTimestamp={timestamps[currentTimestampIndex]}
          fuelTechColors={FUEL_TECH_COLORS}
          allFuelTypes={precomputedChartData?.allFuelTypes || []}
          onGeneratorClick={handleGeneratorClick}
        />

        {/* Feature Inspector Panel - Right Side */}
        {selectedFeature && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bottom: 16,
              zIndex: 1000,
              width: "368px",
              overflow: "auto",
            }}
          >
            <Card
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(33, 33, 33, 0.95)"
                    : "rgba(255, 255, 255, 0.95)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <CardContent
                sx={{
                  flex: 1,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      marginRight: "12px",
                    }}
                  >
                    <Binary size={32} />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: theme.palette.text.primary,
                      }}
                    >
                      Feature Inspector
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Inspect the properties of a selected feature
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                  }}
                >
                  {selectedFeature.properties &&
                    (() => {
                      const groupedProperties = groupPropertiesByTable(
                        selectedFeature.properties as Record<
                          string,
                          FeaturePropertyValue
                        >,
                        selectedFeature.layer
                      );
                      const orderedTables = getOrderedTables(
                        groupedProperties,
                        selectedFeature.layer
                      );
                      return orderedTables.map((table, index) => (
                        <Accordion
                          key={table.key}
                          defaultExpanded={index === 0}
                          sx={{
                            "&:before": {
                              display: "none",
                            },
                            boxShadow: "none",
                            backgroundColor: "rgba(0, 0, 0, 0)",
                            backgroundImage: "none",
                            borderRadius: 0,
                            border: "none",
                            "&.Mui-expanded": {
                              margin: 0,
                            },
                            padding: 0,
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              minHeight: "32px",
                              padding: "0",
                              backgroundColor: "rgba(0, 0, 0, 0)",
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              "&.Mui-expanded": {
                                minHeight: "32px",
                              },
                              "& .MuiAccordionSummary-content": {
                                margin: 0,
                                "&.Mui-expanded": {
                                  margin: 0,
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "bold",
                                color: theme.palette.text.primary,
                                textTransform: "capitalize",
                              }}
                            >
                              {table.displayName}
                            </Typography>
                          </AccordionSummary>

                          <AccordionDetails
                            sx={{
                              padding: 0,
                              backgroundColor: "rgba(0, 0, 0, 0)",
                              gap: "4px",
                              marginTop: "4px",
                              marginBottom: "4px",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {Object.entries(table.fields).map(
                              ([fieldName, value]) => (
                                <Box key={fieldName}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "12px",
                                      color: theme.palette.text.secondary,
                                      marginLeft: "16px",
                                    }}
                                  >
                                    {fieldName}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "14px",
                                      color: theme.palette.text.primary,
                                      marginLeft: "12px",
                                    }}
                                  >
                                    {(() => {
                                      if (
                                        value === null ||
                                        value === undefined ||
                                        value === ""
                                      )
                                        return "-";
                                      if (
                                        typeof value === "number" &&
                                        !Number.isInteger(value)
                                      ) {
                                        return value.toFixed(2);
                                      }
                                      return String(value);
                                    })()}
                                  </Typography>
                                </Box>
                              )
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ));
                    })()}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Timeline Slider - Bottom */}
        {isDataLoaded && timestamps.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              width: "60%",
              minWidth: "400px",
              maxWidth: "800px",
            }}
          >
            <Card
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(33, 33, 33, 0.95)"
                    : "rgba(255, 255, 255, 0.95)",
                padding: "16px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsPlaying(!isPlaying)}
                  sx={{ minWidth: "80px" }}
                >
                  {isPlaying ? "Pause" : "Play"}
                </Button>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary,
                      marginBottom: "8px",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {timestamps[currentTimestampIndex]}
                  </Typography>

                  <Slider
                    value={currentTimestampIndex}
                    min={0}
                    max={timestamps.length - 1}
                    onChange={(_, value) => {
                      setCurrentTimestampIndex(value as number);
                      setIsPlaying(false);
                    }}
                    marks={sliderMarks}
                    sx={{
                      "& .MuiSlider-markLabel": {
                        fontSize: "9px",
                        whiteSpace: "pre-line",
                        transform: "translateX(-50%)",
                        textAlign: "center",
                        lineHeight: "1.2",
                        "&[data-index='0']": {
                          transform: "translateX(0)",
                        },
                      },
                      "& .MuiSlider-mark": {
                        height: "8px",
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Card>
          </Box>
        )}
      </Basemap>
    </>
  );
};
